import { Divider, Group, Modal, Stack, Button, Tooltip } from '@mantine/core';
import Badge, { BADGE_LIGHT_STYLES } from '@ui/components/Badge/Badge';
import Text from '@ui/components/Text/Text';
import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import useStackeableModal from 'src/new_arch/hooks/useStackeableModal';
import useDelayedOpening from 'src/new_arch/hooks/useDelayedOpening';

import Checkbox from '../../../../../components/UI/Checkbox/Checkbox';
import { PRIMARY_BASE } from '../../../../../styles/colors';
import useContactsGroupsModalPresenter from './presenter/useContactsGroupsModalPresenter';
import Workspace from '../../../../../entities/workspace';
import FeaturePaywall from '../../components/FeaturePaywall/FeaturePaywall';
import PricingPlanFeature from '../../../../../entities/pricing/pricingPlanFeature';

import './ContactsGroupsModal.scss';

type ContactsGroupsModalProps = {
	workspace: Workspace;
	onClose: () => void;
	onConfirm: ( emails: string[] ) => void;
	stackOrder?: number;
}

const DEFAULT_STYLES = {
	header: { borderBottom: '1px solid var(--mantine-color-gray-1)' },
	body: { padding: 0, marginTop: 8 }
}

const ContactsGroupsModal = ( { workspace, onClose, onConfirm, stackOrder = 1 } : ContactsGroupsModalProps ) => {
	const presenter = useContactsGroupsModalPresenter( workspace );
	const isFeatureAvailable = workspace?.supportsFeature( PricingPlanFeature.CONTACT_GROUPS ) || false;

	useEffect( () => {
		presenter.onMount().then( () => presenter.setLoading( false ) );
	}, [] );

	const handleCheckboxChange = ( groupID: number ) : void => presenter.toggleGroupCheck( { groupID } );

	const uncheckAllGroups = () => {
		presenter.uncheckAllGroups();
	};

	const onModalClosed = () => {
		onClose();
		uncheckAllGroups();
	}

	const onAddEmailsClicked = () => {
		const emails = presenter.getEmailsFromCheckedGroups();
		onConfirm( emails );
		uncheckAllGroups();
		onModalClosed();
	};

	const onSettingsClicked = () => (
		<a className="linkToSettings" href={`/user/workspaces/${presenter.workspaceID}/projects`}>workspace settings</a>
	);

	const opened = useDelayedOpening();
	const { modalStyles } = useStackeableModal( { stackOrder, defaultStyles: DEFAULT_STYLES } );

	const renderActions = () => (
		<div className="actions">
			{presenter.thereAreGroups ? (
				<div className="contact-groups-buttons">
					<Button variant="white" size="sm" color="dark" onClick={onModalClosed}>Cancel</Button>
					<Button size="sm" onClick={onAddEmailsClicked} disabled={!isFeatureAvailable}>Add</Button>
				</div>
			) : <Button size="sm" onClick={onModalClosed}>Got It</Button>}
		</div>
	);

	const renderFooter = () => (
		<div className="contact-groups-footer">
			<p>You can create or modify from {onSettingsClicked()}</p>
		</div>
	);

	const renderContactsGroups = () => (
		<div className="contact-groups">
			{presenter.getContactsGroups.map( group => (
				<div className="GroupItem" key={group.id}>
					<Checkbox
						key={group.name}
						label={group.name}
						onChange={() => handleCheckboxChange( group.id )}
						value={presenter.isGroupChecked( { groupID: group.id } )}
						checkedColor={PRIMARY_BASE}
						disabled={!isFeatureAvailable}
					/>
				</div>
			) ) }
		</div>
	);

	const renderEmptyState = () => (
		<div className="emptyState">
			<p>There aren&apos;t any groups available.</p>
			{presenter.userCanManageContactsGroups
				? <p>Create or modify from<br /> {onSettingsClicked()}</p>
				: (
					<p>Only owners and members can<br />create or modify from workspace<br />settings</p>
				)}
		</div>
	);

	const renderContent = () => {
		if ( presenter.isLoading ) return ( <p className="loading">Loading...</p> );
		return presenter.thereAreGroups ? renderContactsGroups() : renderEmptyState();
	}

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={(
				<Group spacing={10}>
					Collaborator Groups
					{!presenter.userCanManageContactsGroups && (
						<Badge size="sm" color="blue" variant="light">Only Managers</Badge>
					)}
					{presenter.userCanManageContactsGroups && (
						<FeaturePaywall
							feature={PricingPlanFeature.CONTACT_GROUPS}
							workspace={workspace}
							stackOrder={stackOrder + 1}
							origin="contactsGroupsModal"
						>
							{( { planName, paywallMessage } ) => (
								!isFeatureAvailable && (
									<Tooltip
										label={<div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{paywallMessage}</div>}
										withArrow
										position="top"
										offset={4}
										withinPortal
										arrowPosition="center"
									>
										<Badge color="green" size="xs" variant="light" styles={BADGE_LIGHT_STYLES}>
											{planName}
										</Badge>
									</Tooltip>
								)
							)}
						</FeaturePaywall>
					)}
				</Group>
			)}
			styles={modalStyles}
			size={343}
		>
			<div className="ContactsGroupsModal">
				{presenter.userCanManageContactsGroups ? (
					<>
						{renderContent()}
						{!presenter.isLoading && renderActions()}
						{( !presenter.isLoading && presenter.thereAreGroups ) && renderFooter()}
					</>
				) : (
					<Stack spacing={0}>
						<Text align="center" p="16px 24px 24px 24px">
							Only managers can use and manage contact groups.{' '}
							You can request the workspace owner to upgrade your seat.
						</Text>
						<Divider size={1} color="gray.2" />
						<Group position="right" p="6px 12px">
							<Button size="sm" onClick={onModalClosed}>Close</Button>
						</Group>
					</Stack>
				)}
			</div>
		</Modal>
	);
}

export default observer( ContactsGroupsModal );
